import { useEffect, useMemo, useState } from "react";

import { drugReferenceService, lookupId } from "services/drugReferenceService";
import { findMedicationInteractions, isCurrentMedication } from "utils/drugInteractions";
import { findAllergyConflicts, findConditionConflicts } from "utils/medicationSafety";

/**
 * The annotated drugLabels document behind a stored rxcui, or behind a typed name through the
 * brand-name aliases ("Coumadin" → warfarin). Null when neither leads to one. Cached per session.
 */
async function labelFor(rxcui, name) {
  const reference = await drugReferenceService.getReferenceFor({ rxcui, name });
  return reference?.labelSetId ? drugReferenceService.getLabel(reference.labelSetId) : null;
}

/**
 * Everything the FDA labels say about the patient's current medications against each other,
 * their recorded allergies, and their recorded conditions.
 *
 * Returns `{ interactions, allergyConflicts, conditionConflicts, checkedCount, uncheckedCount,
 * loading }`. Medications saved from the verified search are found by rxcui, hand-typed ones by
 * name through the brand-name aliases ("Coumadin"); any that still lead to no label are counted
 * in `uncheckedCount` so the page can say so rather than imply they were cleared. Allergies
 * resolve the same way and bring their own label, so an allergy to amoxicillin covers the rest
 * of the penicillins.
 */
export function useMedicationSafety(medications, allergies = [], conditions = []) {
  const current = useMemo(
    () => (medications || []).filter(isCurrentMedication),
    [medications]
  );
  const allergyList = useMemo(() => (Array.isArray(allergies) ? allergies : []), [allergies]);

  // Refetch only when the medications or drug allergies change, not on every snapshot.
  const signature = [
    current.map((m) => `${m.id}:${lookupId({ rxcui: m.rxcui, name: m.name })}`).join("|"),
    allergyList.map((a) => lookupId({ rxcui: a?.rxcui, name: a?.allergen || a })).join("|"),
  ].join("#");
  const [state, setState] = useState({ signature: null, entries: [], allergyEntries: [] });

  useEffect(() => {
    let live = true;
    Promise.all([
      Promise.all(
        current.map(async (m) => ({ medication: m, label: await labelFor(m.rxcui, m.name) }))
      ),
      Promise.all(
        allergyList.map(async (a) => ({
          allergy: a,
          label: await labelFor(a?.rxcui, typeof a === "string" ? a : a?.allergen),
        }))
      ),
    ]).then(([entries, allergyEntries]) => {
      if (live) setState({ signature, entries, allergyEntries });
    });
    return () => {
      live = false;
    };
    // signature stands in for `current` and `allergyList`; see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return useMemo(() => {
    const loading = state.signature !== signature;
    const checked = loading ? [] : state.entries.filter((e) => e.label?.selfKeys);
    // An allergy is matched by its wording even when its own drug label is not loaded.
    const allergyEntries = loading
      ? []
      : state.allergyEntries.map((a) => ({ ...a, label: a.label?.selfKeys ? a.label : null }));
    return {
      interactions: findMedicationInteractions(checked),
      allergyConflicts: findAllergyConflicts(checked, allergyEntries),
      conditionConflicts: findConditionConflicts(checked, conditions),
      checkedCount: checked.length,
      uncheckedCount: loading ? 0 : current.length - checked.length,
      loading,
    };
  }, [state, signature, current.length, conditions]);
}
